























































































































































































import {
	computed,
	defineComponent,
	PropType,
	Ref,
	ref,
	watch,
} from '@vue/composition-api';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart, LineChart } from 'echarts/charts';
import {
	GridComponent,
	TitleComponent,
	TooltipComponent,
	LegendComponent,
	DataZoomComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';
import { DataPointName, DateData, EchartsHookState } from '@front-app/hooks/DataDashboard/model';
import {
	GroupDataBucket,
	GroupDataRange,
	GroupDataType,
} from '@front-app/services/GroupsApi/GroupDataModel';
import sub from 'date-fns/sub';
import startOfDay from 'date-fns/startOfDay/index';
import { GroupsStore } from '@front-app/store/singletons/Groups';
import { getEchartsStore } from '@front-app/store/modules/echarts/echartGraph';
import { useEchartWithGroupMemberData } from '@front-app/hooks/DataDashboard/useGroupMemberDataService';
import { useEchartWithShareData } from '@front-app/hooks/DataDashboard/useShareDataService';
import { useEchartWithDiscussionTopicData } from '@front-app/hooks/DataDashboard/useDiscussionTopicDataService';
import { useEchartWithGroupEventMemberData } from '@front-app/hooks/DataDashboard/useGroupEventMemberDataService';
import { useGroupEventCreationData } from '@front-app/hooks/DataDashboard/useGroupEventCreationDataService';
import { useEchartWithDiscussionViewData } from '@front-app/hooks/DataDashboard/useDiscussionViewDataService';
import { useEchartWithDiscussionPostData } from '@front-app/hooks/DataDashboard/useDiscussionPostDataService';
import { useEchartWithGroupEventViewData } from '@front-app/hooks/DataDashboard/useGroupEventViewDataService';
import { useEchartWithGroupViewData } from '@front-app/hooks/DataDashboard/useGroupViewDataService';
import { useEchartWithFileDownloadData } from '@front-app/hooks/DataDashboard/useGroupFileDownloadDataService';
import {
	JsonArray,
} from 'json-to-csv-in-browser';
import { SortDirection } from '@front-app/components/dataDashboard/model';
import { DOWNLOAD_ICON } from '@front-app/vendor/icon-definitions';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import ColumnContainer from '@front-app/components/layouts/columns/ColumnContainer.vue';
import ColumnHeader from '@front-app/components/layouts/columns/ColumnHeader.vue';
import PaddedContent from '@front-app/components/layouts/columns/PaddedContent.vue';
import RadioList from '@front-app/components/inputs/RadioList.vue';
import DataTable from '@front-app/components/dataDashboard/DataTable.vue';
import LoaderState from '@front-app/components/LoaderState.vue';
import { Size } from '@front-app/components/constants';
import { CurrentUserStore } from '@front-app/store/singletons/User';
import { canViewEnhancedDashboard, hasAdminPrivileges } from '@front-app/util/permission';
import SimpleCard from '@front-app/components/cards/SimpleCard.vue';
import {
	DEFAULT_RANGE,
	DEFAULT_SORT_BY,
	DEFAULT_SORT_DIRECTION,
	DEFAULT_SOURCE,
	GroupDataQuery,
} from '@front-app/router/util/groupDataQuery';
import deepEqual from 'deep-equal';
import { DEFAULT_AVERAGING_WINDOW_DAYS, localDateToUtc, utcToLocalDate } from '@front-app/hooks/DataDashboard/util';
import { setInQuery } from '@front-app/util/routing';
import { Routes } from '@front-app/constants/routes';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { SelectBoxChoice } from '@front-app/components/inputs/SelectBoxModel';
import { sortByAlphabeticalDesc, sortBySelectedFirst, useCheckboxes } from '@front-app/hooks/useCheckboxes';
import { syncedRef } from '@front-app/util/reactivity';
import CheckboxList from '@front-app/components/inputs/CheckboxList.vue';
import HelpTooltip from '@front-app/components/dropdowns/HelpTooltip.vue';
import HelpTooltipBlock from '@front-app/components/dropdowns/HelpTooltipBlock.vue';
import TimeRangeSelector from '@front-app/modules/dataDashboards/components/TimeRangeSelector.vue';
import { useAdminAccess } from '@front-app/modules/group/hooks/useAdminAccess';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import PremiumTooltip from '@front-app/modules/dataDashboards/components/PremiumTooltip.vue';
import ContactUsLink from '@front-app/components/ContactUsLink.vue';
import {
	DATA_TABLE_COLUMNS,
	DATA_SOURCE_BUTTON_LIST,
	DATA_SOURCE_ICONS,
	DATA_SOURCE_LABELS,
	SORT_MAP,
	DEFAULT_SORT,
} from './dataDashboard';

use([
	GridComponent,
	CanvasRenderer,
	BarChart,
	LineChart,
	TitleComponent,
	TooltipComponent,
	LegendComponent,
	DataZoomComponent,
]);

export default defineComponent({
	components: {
		VChart,
		PaddedContent,
		ColumnContainer,
		ColumnHeader,
		ErrorCard,
		RadioList,
		DataTable,
		LoaderState,
		SimpleCard,
		CtaButton,
		FontAwesomeIcon,
		CheckboxList,
		HelpTooltip,
		HelpTooltipBlock,
		TimeRangeSelector,
		PremiumTooltip,
		ContactUsLink,
	},
	props: {
		groupId: {
			type: String,
			required: true,
		},
		currentDataSource: {
			type: String as PropType<GroupDataType>,
			default: DEFAULT_SOURCE,
		},
		currentDataRange: {
			type: Number as PropType<GroupDataRange>,
			default: DEFAULT_RANGE,
		},
		currentSortBy: {
			type: String as PropType<DataPointName>,
			default: DEFAULT_SORT_BY,
		},
		currentSortDirection: {
			type: String as PropType<SortDirection>,
			default: DEFAULT_SORT_DIRECTION,
		},
		currentlySelectedCategories: {
			type: Array as PropType<string[]>,
			default: () => [],
		},
		currentlySelectedGroups: {
			type: Array as PropType<string[]>,
			default: () => [],
		},
	},
	setup(props, ctx) {
		const groupsStore = GroupsStore.getInstance();
		const {
			track,
			isError: groupLoadingFailed,
		} = useAsyncStatus();
		track(GroupsStore.getInstance().fetchGroupsOnce());

		const group = computed(() => groupsStore.groupById(props.groupId));
		const { hasDataDashboardAccess } = useAdminAccess(group);

		const isEnhancedDashboard = computed(() => canViewEnhancedDashboard(group.value));

		const adminnedGroups = computed(() => groupsStore.groups.value
			.filter(({ role }) => hasAdminPrivileges(role, CurrentUserStore.isCsr.value)));

		// Routing
		const getSortParams = (newSort: DataPointName, direction: SortDirection) => ({
			[GroupDataQuery.SORT_BY]: newSort,
			[GroupDataQuery.SORT_DIRECTION]: direction,
		});
		const routeToSort = (newSort: DataPointName) => {
			let direction = DEFAULT_SORT_DIRECTION;
			if (newSort === props.currentSortBy) {
				direction = props.currentSortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
			}
			const params = getSortParams(newSort, direction);
			const newRoute = setInQuery(ctx.root.$router, ctx.root.$route, params);
			ctx.root.$router.push(newRoute);
		};
		const setQuery = (queryParam: GroupDataQuery, value: string | (Array<string | null>)) => {
			const params = getSortParams(DEFAULT_SORT_BY, DEFAULT_SORT_DIRECTION);
			const newRoute = setInQuery(ctx.root.$router, ctx.root.$route, { ...params, [queryParam]: value });
			ctx.root.$router.push(newRoute);
		};
		// END Routing

		// Group selector
		const adminnedGroupIds: Ref<string[]> = computed(() => adminnedGroups.value
			.map(({ id }) => id));
		const initialGroupIds = syncedRef(() => props.currentlySelectedGroups).ref;
		const {
			choices: groupsButtonList,
			selectedChoices: selectedGroupIds,
			toggle: toggleGroupId,
		} = useCheckboxes(ctx, {
			choiceKeys: adminnedGroupIds,
			initialChoices: initialGroupIds,
			labelFn: (groupId: string) => (groupsStore.groupById(groupId)?.name || ''),
			sortFns: [sortBySelectedFirst, sortByAlphabeticalDesc],
		});
		const applyGroupIdChanges = () => {
			setQuery(GroupDataQuery.GROUP, selectedGroupIds.value);
		};
		// END Group selector

		const {
			lineOption,
			setSeries,
		} = getEchartsStore(ctx);

		const allData: Ref<DateData[]> = ref([]);

		const hookState: Ref<EchartsHookState> = computed(() => ({
			isEnhancedDashboard: isEnhancedDashboard.value,
			groupIds: selectedGroupIds.value.length > 0 ? selectedGroupIds.value : [props.groupId],
			dateTimeStart: utcToLocalDate(getDaysAgo(props.currentDataRange)),
			dateTimeEnd: getToday(),
			bucketSize: GroupDataBucket.ONE_DAY,
			averagingWindowDays: DEFAULT_AVERAGING_WINDOW_DAYS,
			categoryNames: props.currentlySelectedCategories,
			aggregateGroups: (selectedGroupIds.value.length > 1),
		}));

		const updatersByType: Record<GroupDataType, () => Promise<void>> = {
			[GroupDataType.MEMBERS]: useEchartWithGroupMemberData(hookState, allData, setSeries, ctx).update,
			[GroupDataType.SHARES]: useEchartWithShareData(hookState, allData, setSeries, ctx).update,
			[GroupDataType.DISCUSSION_TOPICS]: useEchartWithDiscussionTopicData(hookState, allData, setSeries, ctx).update,
			[GroupDataType.DISCUSSION_VIEWS]: useEchartWithDiscussionViewData(hookState, allData, setSeries, ctx).update,
			[GroupDataType.DISCUSSION_POSTS]: useEchartWithDiscussionPostData(hookState, allData, setSeries, ctx).update,
			[GroupDataType.EVENT_MEMBERS]: useEchartWithGroupEventMemberData(hookState, allData, setSeries, ctx).update,
			[GroupDataType.EVENT_VIEWS]: useEchartWithGroupEventViewData(hookState, allData, setSeries, ctx).update,
			[GroupDataType.EVENT_CREATIONS]: useGroupEventCreationData(hookState, allData, setSeries, ctx).update,
			[GroupDataType.GROUP_VIEWS]: useEchartWithGroupViewData(hookState, allData, setSeries, ctx).update,
			[GroupDataType.FILE_DOWNLOADS]: useEchartWithFileDownloadData(hookState, allData, setSeries, ctx).update,
		};

		const {
			track: trackDataFetch,
			isLoading,
			isError,
		} = useAsyncStatus();
		const updateData = async () => {
			await trackDataFetch(updatersByType[props.currentDataSource]());
		};

		watch(() => [
			props.currentlySelectedGroups,
			props.currentDataSource,
			props.currentDataRange,
			props.currentlySelectedCategories,
		], (curr, prev) => {
			// It is necessary to use a deep comparison rather than a regular equality because the identity
			// of the "currentlySelectedCategories" _always_ changes
			if (deepEqual(curr, prev)) {
				return;
			}
			updateData();
		}, { immediate: true });

		const currentColumns = computed(() => DATA_TABLE_COLUMNS[props.currentDataSource]);
		const sortedData = computed(() => {
			const sorter = SORT_MAP?.[props.currentSortBy]?.[props.currentSortDirection] || DEFAULT_SORT;
			return allData.value.map((a) => a).sort(sorter);
		});

		const summaryData = computed(() => {
			const data = allData.value;
			const initialValue = data[0].totalAtDate;
			const finalValue = allData.value[allData.value.length - 1].totalAtDate;
			const delta = finalValue - initialValue;
			return {
				initialValue,
				finalValue,
				delta,
			};
		});

		// CSV Download
		const doCsvDownload = () => {
			const headers = [
				ctx.root.$t(currentColumns.value.dateTitle),
				...currentColumns.value.dataPointTitles.map(({ title }) => ctx.root.$t(title) as string),
				ctx.root.$t(currentColumns.value.totalTitle),
			];
			const extractDataForHeaders = (theData: Record<DataPointName, number>) => currentColumns
				.value
				.dataPointTitles
				.map((title) => Math.abs(theData[title.dataPoint]));
			const result = new JsonArray([
				headers,
				...sortedData.value.map(({ date, data, totalAtDate }) => [date.toISOString(), ...extractDataForHeaders(data), totalAtDate]),
			]);
			const link = document.createElement('a');
			link.href = `data:text/csv;charset=utf-8,${window.encodeURI(result.convertToCSVstring())}`;
			link.download = `TimedRight - ${group.value?.name} - Data - ${(new Date()).toISOString()}.csv`;
			link.click();
		};
		// END CSV Download

		// Categories
		const locallySelectedCategories: Ref<string[]> = ref([]);
		watch(() => props.currentlySelectedCategories, (curr) => {
			locallySelectedCategories.value = curr;
		}, { immediate: true });
		const showCategoryPicker = computed(() => props.currentDataSource === GroupDataType.DISCUSSION_POSTS
				&& ((group.value?.categories?.length || 0) > 0));
		const categoryChoices = computed((): SelectBoxChoice[] => (group.value?.categories || []).map((category) => ({
			key: category,
			label: category,
		})));
		const applyCategoryChanges = () => {
			setQuery(GroupDataQuery.CATEGORY, locallySelectedCategories.value);
		};
		const clearCategories = () => {
			locallySelectedCategories.value = [];
			applyCategoryChanges();
		};
		const toggleCategory = (category: string) => {
			const current = locallySelectedCategories.value;
			if (current.includes(category)) {
				locallySelectedCategories.value = current.filter((existing) => existing !== category);
			} else {
				locallySelectedCategories.value = [...current, category];
			}
		};

		return {
			Size,
			hasDataDashboardAccess,
			groupLoadingFailed,
			isLoading,
			isError,
			GroupDataQuery,
			GROUP_MAIN_ROUTE: Routes.GROUP_MAIN,
			// Enhanced dashboard
			ButtonPalettes,
			isEnhancedDashboard,
			// Routing
			setQuery,
			routeToSort,
			// Group selector
			groupsButtonList,
			selectedGroupIds,
			toggleGroupId,
			applyGroupIdChanges,
			// Source filter
			DATA_SOURCE_BUTTON_LIST,
			DATA_SOURCE_ICONS,
			DATA_SOURCE_LABELS,
			// Graphs
			group,
			lineOption,
			// Table
			sortedData,
			currentColumns,
			// Summary data
			hookState,
			summaryData,
			// CSV Download
			DOWNLOAD_ICON,
			doCsvDownload,
			// Category picker
			locallySelectedCategories,
			showCategoryPicker,
			categoryChoices,
			clearCategories,
			toggleCategory,
			applyCategoryChanges,
			// Util
			getGroupName(id: string): string {
				return groupsStore.groupById(id)?.name || '';
			},
		};
	},
});

function getDaysAgo(days: number): Date {
	return localDateToUtc(startOfDay(sub(new Date(), { days })));
}

function getToday(): Date {
	return startOfDay(new Date());
}
